import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Send, Loader2, MessageSquare, User } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple markdown renderer for chat messages
const renderMarkdown = (text) => {
  if (!text) return null;
  
  // Split into lines for processing
  const lines = text.split('\n');
  const elements = [];
  let currentList = [];
  let listType = null;
  
  const flushList = () => {
    if (currentList.length > 0) {
      if (listType === 'ul') {
        elements.push(<ul key={`ul-${elements.length}`} className="list-disc list-inside my-2 space-y-1">{currentList}</ul>);
      } else {
        elements.push(<ol key={`ol-${elements.length}`} className="list-decimal list-inside my-2 space-y-1">{currentList}</ol>);
      }
      currentList = [];
      listType = null;
    }
  };
  
  const processInline = (line) => {
    // Bold: **text** or __text__
    line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    line = line.replace(/__(.+?)__/g, '<strong>$1</strong>');
    // Italic: *text* or _text_ (not within words)
    line = line.replace(/(?<![*\w])\*([^*]+)\*(?![*\w])/g, '<em>$1</em>');
    // Code: `text`
    line = line.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-black/10 rounded text-sm">$1</code>');
    return line;
  };
  
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    
    // Headers
    if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(<h4 key={idx} className="font-semibold text-sm mt-3 mb-1">{trimmed.slice(4)}</h4>);
    } else if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(<h3 key={idx} className="font-semibold mt-3 mb-1">{trimmed.slice(3)}</h3>);
    } else if (trimmed.startsWith('# ')) {
      flushList();
      elements.push(<h2 key={idx} className="font-bold mt-3 mb-1">{trimmed.slice(2)}</h2>);
    }
    // Bullet lists
    else if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.startsWith('* ')) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      const content = trimmed.slice(2);
      currentList.push(<li key={idx} dangerouslySetInnerHTML={{ __html: processInline(content) }} />);
    }
    // Numbered lists
    else if (/^\d+\.\s/.test(trimmed)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      const content = trimmed.replace(/^\d+\.\s/, '');
      currentList.push(<li key={idx} dangerouslySetInnerHTML={{ __html: processInline(content) }} />);
    }
    // Empty line
    else if (trimmed === '') {
      flushList();
    }
    // Regular paragraph
    else {
      flushList();
      elements.push(<p key={idx} className="my-1" dangerouslySetInnerHTML={{ __html: processInline(trimmed) }} />);
    }
  });
  
  flushList();
  return elements;
};

const FarmChat = () => {
  const { getAuthHeader, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/chat/history`, {
        headers: getAuthHeader()
      });
      const history = res.data.history || [];
      const formatted = [];
      for (const item of history) {
        formatted.push({ role: 'user', content: item.message, id: `${item.id}-user` });
        formatted.push({ role: 'assistant', content: item.response, id: `${item.id}-assistant` });
      }
      setMessages(formatted);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage, id: Date.now() }]);
    setLoading(true);

    try {
      const res = await axios.post(
        `${API_URL}/api/chat/send`,
        { message: userMessage },
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
      );
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: res.data.response, 
        id: res.data.id 
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.', 
        id: Date.now() 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    "What should I check on my livestock today?",
    "How do I improve soil health in dry conditions?",
    "When is the best time to irrigate?",
    "What are signs of heat stress in cattle?"
  ];

  return (
    <div className="flex flex-col h-[calc(100dvh-10rem)] md:h-[calc(100vh-8rem)] lg:h-[calc(100vh-6rem)]" data-testid="farm-chat">
      <div className="mb-2 flex-shrink-0">
        <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground">
          Farm Chat
        </h1>
        <p className="text-muted-foreground text-sm mt-0.5">
          Ask anything about your farm
        </p>
      </div>

      {/* Messages Area */}
      <Card className="card-farm flex-1 overflow-hidden flex flex-col min-h-0">
        <CardContent className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 md:space-y-4 scrollbar-thin">
          {historyLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 space-y-6">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-primary" />
              </div>
              <div className="text-center">
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  Welcome to Farm Chat
                </h3>
                <p className="text-muted-foreground text-sm max-w-sm">
                  Ask questions about livestock care, crop management, 
                  weather impacts, or general farming topics.
                </p>
              </div>
              <div className="grid gap-2 w-full max-w-md">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q)}
                    className="text-left p-3 rounded-lg bg-muted/50 hover:bg-muted text-sm text-foreground transition-colors"
                    data-testid={`suggested-question-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg) => (
              <div 
                key={msg.id}
                className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''} animate-fade-in`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'user' ? 'bg-primary/20' : 'bg-muted'
                }`}>
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4 text-primary" />
                  ) : (
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
                <div className={`max-w-[80%] p-4 rounded-2xl ${
                  msg.role === 'user' 
                    ? 'bg-primary text-primary-foreground rounded-tr-sm' 
                    : 'bg-muted text-foreground rounded-tl-sm'
                }`}>
                  {msg.role === 'user' ? (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  ) : (
                    <div className="text-sm">{renderMarkdown(msg.content)}</div>
                  )}
                </div>
              </div>
            ))
          )}
          
          {loading && (
            <div className="flex gap-3 animate-fade-in">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="bg-muted p-4 rounded-2xl rounded-tl-sm">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input Area */}
        <div className="border-t border-border/60 p-3 md:p-4 flex-shrink-0">
          <form onSubmit={sendMessage} className="flex gap-2 md:gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your farm..."
              className="input-farm flex-1 text-base"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              className="btn-primary px-3 md:px-4" 
              disabled={loading || !input.trim()}
              data-testid="chat-send-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default FarmChat;
